// src/api/fetchAssessmentQuestions.ts
//
// Single source of truth: DATABASE via RPCs and views.
// JSON files are NOT imported at runtime — they exist only for DB seed migrations.
//
import { supabase } from "@/integrations/supabase/client";

/** Row shape used by the Wizard & DomainSection */
export type AssessmentQuestionRow = {
  questionnaire_id: string;
  question_id: number;
  question_key: string;
  order_index: number;
  domain: string; // GV | ID | PR | DE | RS | RC
  title: string | null;
  question_text: string;
  evidence_json: any; // string[] basically

  // may be present (to show long descriptions in UI)
  answer_scale?: Record<string, string> | null;

  // controls from the DB
  controls?: string[] | null;

  // evidence requirement config from questionnaire_items
  requires_evidence?: boolean;
  evidence_threshold?: number | null;

  // merged response fields (optional)
  score?: number | null;
  comment?: string | null;
  evidence_links?: string[] | null;

  // reviewer ratings (for PM view)
  reviewer_score?: number | null;
  reviewer_notes?: string | null;
};

type Args = {
  assessmentId: string;
  questionnaireId: string;
  includeReviewerData?: boolean; // for PM view
  isAdmin?: boolean; // use admin RPC for responses
  isPM?: boolean; // use PM RPCs for questionnaire items and responses
  portfolioId?: string; // required for PM RPCs
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Fetch respondent/admin/PM responses and return as a Map keyed by question_id */
async function fetchResponses(
  assessmentId: string,
  ids: number[],
  isAdmin: boolean,
  isPM: boolean = false,
  portfolioId?: string
) {
  type ResponseVal = { score: number | null; comment: string | null; evidence_links: string[] | null };
  const byId = new Map<number, ResponseVal>();
  if (ids.length === 0) return byId;

  if (isAdmin) {
    console.log("[LOAD RESPONSES] admin rpc", { assessmentId, count: ids.length });
    const { data, error } = await (supabase.rpc as any)("admin_list_responses", {
      p_assessment_id: assessmentId,
      p_question_ids: ids.map(Number),
    });
    console.log("[LOAD RESPONSES] admin rpc result", { assessmentId, count: data?.length, error });
    if (!error && Array.isArray(data)) {
      data.forEach((r: any) => {
        if (!r || typeof r.question_id !== "number") return;
        byId.set(r.question_id, {
          score: r.score ?? null,
          comment: r.comment ?? null,
          evidence_links: (r.evidence_links as string[] | null) ?? null,
        });
      });
    }
    return byId;
  }

  // PM branch: use pm_get_responses
  if (isPM && portfolioId) {
    const { data, error } = await (supabase.rpc as any)("pm_get_responses", {
      p_portfolio_id: portfolioId,
      p_assessment_id: assessmentId,
    });
    if (!error && Array.isArray(data)) {
      data.forEach((r: any) => {
        if (!r || typeof r.question_id !== "number") return;
        byId.set(r.question_id, {
          score: r.score ?? null,
          comment: r.comment ?? null,
          evidence_links: (r.evidence_links as string[] | null) ?? null,
        });
      });
    } else if (error) {
      console.warn("[fetchAssessmentQuestions] pm_get_responses RPC error:", error);
    }
    return byId;
  }

  // Respondent / Reviewer: use SECURITY DEFINER RPC
  const { data: resp, error: respErr } = await (supabase.rpc as any)(
    "respondent_get_responses",
    { p_assessment_id: assessmentId }
  );

  if (!respErr && Array.isArray(resp)) {
    resp.forEach((r: any) => {
      if (!r || typeof r.question_id !== "number") return;
      byId.set(r.question_id, {
        score: r.score ?? null,
        comment: r.comment ?? null,
        evidence_links: (r.evidence_links as string[] | null) ?? null,
      });
    });
  } else if (respErr) {
    console.warn("[fetchAssessmentQuestions] respondent_get_responses RPC error:", respErr);
  }
  return byId;
}

/** Fetch reviewer ratings and return as a Map keyed by question_id */
async function fetchReviewerRatings(
  assessmentId: string,
  ids: number[],
  includeReviewerData: boolean,
  isAdmin: boolean,
) {
  type RatingVal = { reviewer_score: number | null; reviewer_notes: string | null };
  const map = new Map<number, RatingVal>();
  if (!includeReviewerData || ids.length === 0) return map;

  // Admin or PM with includeReviewerData: use appropriate RPC
  if (isAdmin) {
    console.log("[LOAD REVIEWER RATINGS] admin rpc", { assessmentId });
    const { data, error } = await (supabase.rpc as any)("admin_list_reviewer_ratings", {
      p_assessment_id: assessmentId,
    });
    if (error) {
      console.warn("[fetchAssessmentQuestions] admin_list_reviewer_ratings error:", error);
      return map;
    }
    (data || []).forEach((r: any) => {
      if (ids.includes(r.question_id)) {
        map.set(r.question_id, {
          reviewer_score: r.reviewer_score ?? null,
          reviewer_notes: r.reviewer_notes ?? null,
        });
      }
    });
    return map;
  }

  // PM: use pm_get_reviewer_ratings
  const { data: pmData, error: pmErr } = await (supabase.rpc as any)(
    "pm_get_reviewer_ratings",
    { p_assessment_id: assessmentId }
  );
  if (!pmErr && Array.isArray(pmData)) {
    pmData.forEach((r: any) => {
      map.set(r.question_id, {
        reviewer_score: r.reviewer_score ?? null,
        reviewer_notes: r.reviewer_notes ?? null,
      });
    });
    return map;
  }

  // Fallback: respondent RPC
  const { data, error } = await (supabase.rpc as any)(
    "respondent_get_reviewer_ratings",
    { p_assessment_id: assessmentId }
  );

  if (error) {
    console.warn("[fetchAssessmentQuestions] respondent_get_reviewer_ratings RPC error:", error);
    return map;
  }

  (data || []).forEach((r: any) => {
    map.set(r.question_id, {
      reviewer_score: r.reviewer_score ?? null,
      reviewer_notes: r.reviewer_notes ?? null,
    });
  });
  return map;
}

/** Fetch questionnaire items from the DB (RPC for respondents, PM RPC for portfolio managers, view for admin) */
async function fetchQuestionnaireItems(
  assessmentId: string,
  questionnaireId: string,
  isAdmin: boolean,
  isPM: boolean = false,
  portfolioId?: string
): Promise<any[]> {
  // PM branch: use security-definer RPC scoped to portfolio
  if (isPM && portfolioId) {
    const { data, error } = await (supabase.rpc as any)(
      "pm_get_questionnaire_items",
      { p_portfolio_id: portfolioId, p_questionnaire_id: questionnaireId }
    );

    if (error) {
      console.warn("[fetchAssessmentQuestions] pm_get_questionnaire_items RPC error:", error);
      return [];
    }

    return Array.isArray(data) ? data : [];
  }

  if (!isAdmin) {
    // Respondent / Reviewer: use SECURITY DEFINER RPC
    const { data, error } = await (supabase.rpc as any)(
      "respondent_get_questionnaire_items",
      { p_assessment_id: assessmentId, p_questionnaire_id: questionnaireId }
    );

    if (error) {
      console.warn("[fetchAssessmentQuestions] respondent_get_questionnaire_items RPC error:", error);
      return [];
    }

    return Array.isArray(data) ? data : [];
  }

  // Admin / PM: use the view (no respondent-scoped RPC available)
  const { data, error } = await supabase
    .from("v_questionnaire_items_with_controls" as any)
    .select(
      "questionnaire_id, question_id, question_key, order_index, domain, title, question_text, evidence_json, answer_scale, controls"
    )
    .eq("questionnaire_id", questionnaireId)
    .order("order_index", { ascending: true });

  if (error) {
    console.warn("[fetchAssessmentQuestions] view query error:", error);
    return [];
  }

  return Array.isArray(data) ? data : [];
}

/** Normalize a raw item from RPC or view into consistent AssessmentQuestionRow shape */
function normalizeItem(item: any, questionnaireId: string): Partial<AssessmentQuestionRow> {
  // RPC returns evidence_types; view returns evidence_json — unify
  // Unify evidence: view returns {"types": [...]}, RPC returns evidence_types as text[]
  const rawEvidence = item.evidence_json;
  const evidence_json: string[] =
    Array.isArray(rawEvidence) ? rawEvidence
    : (rawEvidence && typeof rawEvidence === "object" && Array.isArray(rawEvidence.types)) ? rawEvidence.types
    : Array.isArray(item.evidence_types) ? item.evidence_types
    : [];

  const controls: string[] = Array.isArray(item.controls) ? item.controls : [];

  return {
    questionnaire_id: item.questionnaire_id ?? questionnaireId,
    question_id: item.question_id,
    question_key: item.question_key ?? "",
    order_index: item.order_index ?? item.display_order ?? 0,
    domain: item.domain ?? "",
    title: item.title ?? null,
    question_text: item.question_text ?? "",
    evidence_json,
    answer_scale: item.answer_scale ?? null,
    controls,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch ordered questions from the DATABASE, merge existing responses,
 * and optionally fetch reviewer ratings.
 *
 * ALL questionnaires now load from the DB. JSON files are NOT used at runtime.
 */
export async function fetchAssessmentQuestions(
  args: Args
): Promise<AssessmentQuestionRow[]> {
  const {
    assessmentId,
    questionnaireId,
    includeReviewerData = false,
    isAdmin = false,
    isPM = false,
    portfolioId,
  } = args;

  // 1) Fetch questionnaire items from DB
  const rawItems = await fetchQuestionnaireItems(assessmentId, questionnaireId, isAdmin, isPM, portfolioId);

  if (rawItems.length === 0) {
    console.warn("[fetchAssessmentQuestions] No items returned for", questionnaireId);
    return [];
  }

  // 2) Normalize items
  const items = rawItems.map((item) => normalizeItem(item, questionnaireId));

  // 3) Collect question IDs for response/rating lookups
  const ids = items
    .map((i) => i.question_id)
    .filter((id): id is number => typeof id === "number");

  // 4) Fetch responses + reviewer ratings in parallel
  const [byId, reviewerRatings] = await Promise.all([
    fetchResponses(assessmentId, ids, isAdmin, isPM, portfolioId),
    fetchReviewerRatings(assessmentId, ids, includeReviewerData, isAdmin),
  ]);

  // 5) Merge everything
  const merged = items.map((item) => {
    const qid = item.question_id as number;
    const r = byId.get(qid);
    const rr = reviewerRatings.get(qid);

    return {
      ...item,
      score: r?.score ?? null,
      comment: r?.comment ?? null,
      evidence_links: r?.evidence_links ?? null,
      reviewer_score: rr?.reviewer_score ?? null,
      reviewer_notes: rr?.reviewer_notes ?? null,
    } as AssessmentQuestionRow;
  });

  console.debug("[fetchAssessmentQuestions] loaded", {
    questionnaireId,
    count: merged.length,
    source: isAdmin ? "view" : "RPC",
  });

  return merged;
}
